/**
 * GPU/viewport diagnostic snapshot. Collects:
 *   - GPU verdict (from the gpuVerdictAtom)
 *   - Renderer string from a fresh on-demand WebGL2 context — the boot
 *     probe drops it after deciding the verdict, so we re-read on demand
 *   - The current series's reconstruction blocker, if any
 *   - Cornerstone image-cache size — the GPU/texture-adjacent memory
 *     pressure that actually maps to viewer behavior. JS heap doesn't:
 *     volumes live in off-heap buffers + GPU VRAM, invisible to V8.
 *
 * All fields are standard ops data per CLAUDE.md (GPU model, verdict,
 * blocker code, cache numbers, navigator.userAgent).
 */

import { cache } from "@cornerstonejs/core";
import { defaultCreateContext, extractRenderer, type GpuVerdict } from "./gpuVerdict";
import { BLOCKER_LABELS, type BlockerCode } from "./reconstructionBlocker";

// Reason precedence lives in `pickViewportComponent.decideViewport` —
// the routing decision and the user-facing label are co-located so a new
// stack trigger can't be added without forcing a matching label. This
// module only formats the diagnostic snapshot for clipboard + popover.

export interface GpuDiagnostic {
  verdict: GpuVerdict;
  blockerCode: BlockerCode | null;
  blockerLabel: string | null;
  renderer: string | null;
  cacheBytesUsed: number;
  cacheBytesMax: number;
  userAgent: string;
}

export interface CollectDeps {
  createContext?: () => WebGL2RenderingContext | null;
  getCacheSize?: () => number;
  getMaxCacheSize?: () => number;
  userAgent?: string;
}

/** Snapshot the diagnostic state. Pure given its deps — production passes
 *  no deps and the function reads real browser APIs; tests inject stubs.
 *
 *  The WebGL2 context is created locally and disposed via
 *  WEBGL_lose_context immediately — browsers cap concurrent contexts
 *  (typically 16), and we don't want popover-spawned probes to evict the
 *  viewport's own contexts. */
export function collectGpuDiagnostic(
  args: { verdict: GpuVerdict; blockerCode: BlockerCode | null | undefined },
  deps: CollectDeps = {},
): GpuDiagnostic {
  const createContext = deps.createContext ?? defaultCreateContext;
  const getCacheSize = deps.getCacheSize ?? (() => cache.getCacheSize());
  const getMaxCacheSize = deps.getMaxCacheSize ?? (() => cache.getMaxCacheSize());
  const userAgent = deps.userAgent ?? navigator.userAgent;

  const gl = createContext();
  const renderer = gl ? extractRenderer(gl) : null;
  if (gl) {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }

  const blockerCode = args.blockerCode ?? null;
  const blockerLabel = blockerCode ? (BLOCKER_LABELS[blockerCode] ?? null) : null;

  return {
    verdict: args.verdict,
    blockerCode,
    blockerLabel,
    renderer,
    cacheBytesUsed: getCacheSize(),
    cacheBytesMax: getMaxCacheSize(),
    userAgent,
  };
}

export function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}

/** Multi-line plaintext for clipboard. Format is stable so support can grep
 *  for fields. Mode line always first. Reason is supplied by the caller
 *  (from `decideViewport(...).reason` upstream); when present and a
 *  blocker code is set, append the raw code in parens so support can
 *  identify a future unmapped code even if the label is generic. */
export function formatDiagnosticForCopy(d: GpuDiagnostic, reason?: string): string {
  const lines: string[] = [];
  lines.push(`Viewport mode: 2D (Stack)`);

  if (reason) {
    const suffix = d.blockerCode ? ` (${d.blockerCode})` : "";
    lines.push(`Reason: ${reason}${suffix}`);
  }

  lines.push(`GPU verdict: ${d.verdict}`);
  lines.push(`Renderer: ${d.renderer ?? "unavailable"}`);
  lines.push(`Image cache: ${formatMB(d.cacheBytesUsed)} / ${formatMB(d.cacheBytesMax)}`);
  lines.push(`User agent: ${d.userAgent}`);

  return lines.join("\n");
}
